import _extends from '@babel/runtime/helpers/extends';
import React, { isValidElement, forwardRef, useState, useRef, useEffect, useCallback, useContext } from 'react';
import _jsx from '@babel/runtime/helpers/jsx';
import _objectSpread from '@babel/runtime/helpers/objectSpread2';
import _defineProperty from '@babel/runtime/helpers/defineProperty';
import treeChanges from 'tree-changes';
import is from 'is-lite';
import { Portal } from '@andes/common';
import includes from '@andes/common/build/utils/includes';
import 'react-dom';
import ExecutionEnvironment from 'exenv';
import { disableWebviewTopbar, enableWebviewTopbar } from '@andes/common/build/utils/webviewTopbarHandler';
import scroll from 'scroll';
import scrollParent from 'scrollparent';
import deepmerge from 'deepmerge';
import Floater from 'react-floater';
import { Button } from '@andes/button';
import Close20 from '@andes/icons/Close20';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import classNames from 'classnames';
import usePreventScroll from '@andes/common/build/hooks/usePreventScroll';
import { getElementsBySelector, addAriaHidden, removeAriaHidden } from '@andes/common/build/hooks/useModal';
import { useId, getPatchedI18n, AndesContext } from '@andes/context';
const ACTIONS = {
  INIT: 'init',
  START: 'start',
  STOP: 'stop',
  RESET: 'reset',
  PREV: 'prev',
  NEXT: 'next',
  GO: 'go',
  CLOSE: 'close',
  SKIP: 'skip',
  UPDATE: 'update'
};
var ACTIONS$1 = ACTIONS;
const EVENTS = {
  TOUR_START: 'tour:start',
  STEP_BEFORE: 'step:before',
  BEACON: 'beacon',
  TOOLTIP: 'tooltip',
  STEP_AFTER: 'step:after',
  TOUR_END: 'tour:end',
  TOUR_STATUS: 'tour:status',
  TARGET_NOT_FOUND: 'error:target_not_found',
  ERROR: 'error'
};
var EVENTS$1 = EVENTS;
const LIFECYCLE = {
  INIT: 'init',
  READY: 'ready',
  BEACON: 'beacon',
  TOOLTIP: 'tooltip',
  COMPLETE: 'complete',
  ERROR: 'error'
};
var LIFECYCLE$1 = LIFECYCLE;
const STATUS = {
  IDLE: 'idle',
  READY: 'ready',
  WAITING: 'waiting',
  RUNNING: 'running',
  PAUSED: 'paused',
  SKIPPED: 'skipped',
  FINISHED: 'finished',
  ERROR: 'error'
};
var STATUS$1 = STATUS;
const {
  canUseDOM
} = ExecutionEnvironment;
function getObjectType(value) {
  return Object.prototype.toString.call(value).slice(8, -1).toLowerCase();
}
function hasOwnProperty(step, key) {
  return Object.prototype.hasOwnProperty.call(step, key);
}
function hasValidKeys(value, keys) {
  if (!is.plainObject(value) || !is.array(keys)) {
    return false;
  }
  return Object.keys(value).every(d => includes(keys, d));
}
function isEqual(left, right) {
  let type;
  const hasReactElement = /*#__PURE__*/isValidElement(left) || /*#__PURE__*/isValidElement(right);
  const hasUndefined = is.undefined(left) || is.undefined(right);
  if (getObjectType(left) !== getObjectType(right) || hasReactElement || hasUndefined) {
    return false;
  }
  if (is.domElement(left) && is.domElement(right)) {
    return left.isSameNode(right);
  }
  if (is.number(left)) {
    return left === right;
  }
  if (is.function(left) && is.function(right)) {
    return left.toString() === right.toString();
  }
  const leftObject = left;
  const rightObject = right;
  for (const key in leftObject) {
    if (hasOwnProperty(leftObject, key)) {
      if (typeof leftObject[key] === 'undefined' || typeof rightObject[key] === 'undefined') {
        return false;
      }
      type = getObjectType(leftObject[key]);
      if (includes(['object', 'array'], type) && isEqual(leftObject[key], rightObject[key])) {
        continue;
      }
      if (type === 'function' && isEqual(leftObject[key], rightObject[key])) {
        continue;
      }
      if (leftObject[key] !== rightObject[key]) {
        return false;
      }
    }
  }
  for (const p in rightObject) {
    if (hasOwnProperty(rightObject, p)) {
      if (typeof leftObject[p] === 'undefined') {
        return false;
      }
    }
  }
  return true;
}
const defaultState = {
  action: ACTIONS$1.INIT,
  controlled: false,
  index: 0,
  lifecycle: LIFECYCLE$1.INIT,
  size: 0,
  status: STATUS$1.IDLE
};
const validKeys = ['action', 'index', 'lifecycle', 'status'];
function createStore(props) {
  const store = new Map();
  const data = new Map();
  class Store {
    constructor(storeProps) {
      var _this = this;
      _defineProperty(this, "listener", void 0);
      _defineProperty(this, "setSteps", steps => {
        const {
          size,
          status
        } = this.getState();
        const state = {
          size: steps.length,
          status
        };
        data.set('steps', steps);
        if (status === STATUS$1.WAITING && !size && steps.length) {
          state.status = STATUS$1.RUNNING;
        }
        this.setState(state);
      });
      _defineProperty(this, "addListener", listener => {
        this.listener = listener;
      });
      _defineProperty(this, "update", state => {
        if (!hasValidKeys(state, validKeys)) {
          throw new Error("State is not valid. Valid keys: ".concat(validKeys.join(', ')));
        }
        this.setState(_objectSpread({}, this.getNextState(_objectSpread(_objectSpread(_objectSpread({}, this.getState()), state), {}, {
          action: state.action || ACTIONS$1.UPDATE
        }), true)));
      });
      _defineProperty(this, "start", nextIndex => {
        const {
          index,
          size
        } = this.getState();
        this.subscriptionIds = disableWebviewTopbar({
          onBack: () => {
            this.stop();
            this.enableWebviewTopbar();
          }
        });
        this.setState(_objectSpread(_objectSpread({}, this.getNextState({
          action: ACTIONS$1.START,
          index: is.number(nextIndex) ? nextIndex : index
        }, true)), {}, {
          status: size ? STATUS$1.RUNNING : STATUS$1.WAITING
        }));
      });
      _defineProperty(this, "stop", function () {
        let advance = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : false;
        const {
          index,
          status
        } = _this.getState();
        if (includes([STATUS$1.FINISHED, STATUS$1.SKIPPED], status)) return;
        _this.setState(_objectSpread(_objectSpread({}, _this.getNextState({
          action: ACTIONS$1.STOP,
          index: index + (advance ? 1 : 0)
        })), {}, {
          status: STATUS$1.PAUSED
        }));
      });
      _defineProperty(this, "close", () => {
        const {
          index,
          status
        } = this.getState();
        if (status !== STATUS$1.RUNNING) return;
        this.setState(_objectSpread({}, this.getNextState({
          action: ACTIONS$1.CLOSE,
          index: index + 1
        })));
      });
      _defineProperty(this, "go", nextIndex => {
        const {
          controlled,
          status
        } = this.getState();
        if (controlled || status !== STATUS$1.RUNNING) return;
        const step = this.getSteps()[nextIndex];
        this.setState(_objectSpread(_objectSpread({}, this.getNextState({
          action: ACTIONS$1.GO,
          index: nextIndex
        })), {}, {
          status: step ? status : STATUS$1.FINISHED
        }));
      });
      _defineProperty(this, "info", () => this.getState());
      _defineProperty(this, "next", () => {
        const {
          index,
          status
        } = this.getState();
        if (status !== STATUS$1.RUNNING) return;
        this.setState(this.getNextState({
          action: ACTIONS$1.NEXT,
          index: index + 1
        }));
      });
      _defineProperty(this, "open", () => {
        const {
          status
        } = this.getState();
        if (status !== STATUS$1.RUNNING) return;
        this.setState(_objectSpread({}, this.getNextState({
          action: ACTIONS$1.UPDATE,
          lifecycle: LIFECYCLE$1.TOOLTIP
        })));
      });
      _defineProperty(this, "prev", () => {
        const {
          index,
          status
        } = this.getState();
        if (status !== STATUS$1.RUNNING) return;
        this.setState(_objectSpread({}, this.getNextState({
          action: ACTIONS$1.PREV,
          index: index - 1
        })));
      });
      _defineProperty(this, "reset", function () {
        let restart = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : false;
        const {
          controlled
        } = _this.getState();
        if (controlled) return;
        _this.setState(_objectSpread(_objectSpread({}, _this.getNextState({
          action: ACTIONS$1.RESET,
          index: 0
        })), {}, {
          status: restart ? STATUS$1.RUNNING : STATUS$1.READY
        }));
      });
      _defineProperty(this, "skip", () => {
        const {
          status
        } = this.getState();
        if (status !== STATUS$1.RUNNING) return;
        this.setState({
          action: ACTIONS$1.SKIP,
          lifecycle: LIFECYCLE$1.INIT,
          status: STATUS$1.SKIPPED
        });
      });
      _defineProperty(this, "enableWebviewTopbar", () => {
        enableWebviewTopbar({
          subscriptionIds: this.subscriptionIds
        });
      });
      const {
        continuous = false,
        step: _step,
        steps: _steps = []
      } = storeProps !== null && storeProps !== void 0 ? storeProps : {};
      this.setState({
        action: ACTIONS$1.INIT,
        controlled: is.number(_step),
        continuous,
        index: is.number(_step) ? _step : 0,
        lifecycle: LIFECYCLE$1.INIT,
        status: _steps.length ? STATUS$1.READY : STATUS$1.IDLE
      }, true);
      this.listener = null;
      this.setSteps(_steps);
    }
    setState(nextState) {
      let initial = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
      const state = this.getState();
      const {
        action,
        index,
        lifecycle,
        size,
        status
      } = _objectSpread(_objectSpread({}, state), nextState);
      store.set('action', action);
      store.set('index', index);
      store.set('lifecycle', lifecycle);
      store.set('size', size);
      store.set('status', status);
      if (initial) {
        store.set('controlled', nextState.controlled);
        store.set('continuous', nextState.continuous);
      }
      if (this.listener && this.hasUpdatedState(state)) {
        this.listener(this.getState());
      }
    }
    getState() {
      if (!store.size) {
        return _objectSpread({}, defaultState);
      }
      return {
        action: store.get('action') || '',
        controlled: store.get('controlled') || false,
        index: parseInt(store.get('index'), 10),
        lifecycle: store.get('lifecycle') || '',
        size: store.get('size') || 0,
        status: store.get('status') || ''
      };
    }
    getNextState(state) {
      let force = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
      const {
        action,
        controlled,
        index,
        size,
        status
      } = this.getState();
      const newIndex = is.number(state.index) ? state.index : index;
      const nextIndex = controlled && !force ? index : Math.min(Math.max(newIndex, 0), size);
      return {
        action: state.action || action,
        controlled,
        index: nextIndex,
        lifecycle: state.lifecycle || LIFECYCLE$1.INIT,
        size: state.size || size,
        status: nextIndex === size ? STATUS$1.FINISHED : state.status || status
      };
    }
    hasUpdatedState(oldState) {
      const before = JSON.stringify(oldState);
      const after = JSON.stringify(this.getState());
      return before !== after;
    }
    getSteps() {
      const steps = data.get('steps');
      return Array.isArray(steps) ? steps : [];
    }
    getHelpers() {
      return {
        close: this.close,
        go: this.go,
        info: this.info,
        next: this.next,
        open: this.open,
        prev: this.prev,
        reset: this.reset,
        skip: this.skip
      };
    }
  }
  return new Store(props);
}
function scrollDoc() {
  return document.scrollingElement || document.createElement('body');
}
function getClientRect(element) {
  if (!element) {
    return null;
  }
  return element.getBoundingClientRect();
}
function getDocumentHeight() {
  const {
    body,
    documentElement: html
  } = document;
  if (!body || !html) {
    return 0;
  }
  return Math.max(body.scrollHeight, body.offsetHeight, html.clientHeight, html.scrollHeight, html.offsetHeight);
}
function getShadowElement(selector) {
  const selectors = [...selector];
  const lastSelector = selectors.pop();
  let element;
  selectors.forEach(sel => {
    const elementQuery = element || document;
    const shadowElement = elementQuery.querySelector(sel.trim());
    if (shadowElement) {
      element = shadowElement.shadowRoot;
    }
  });
  if (element && lastSelector) {
    return element.querySelector(lastSelector);
  }
  return null;
}
function getElement(element) {
  if (Array.isArray(element)) {
    return getShadowElement(element);
  }
  if (typeof element === 'string') {
    return document.querySelector(element);
  }
  return element;
}
function getStyleComputedProperty(el) {
  if (!el || el.nodeType !== 1) {
    return null;
  }
  return getComputedStyle(el);
}
function getScrollParent(element, skipFix, forListener) {
  if (!element) {
    return scrollDoc();
  }
  const parent = scrollParent(element);
  if (parent.isSameNode(scrollDoc())) {
    if (forListener) {
      return document;
    }
    return scrollDoc();
  }
  const hasScrolling = parent.scrollHeight > parent.offsetHeight;
  if (!hasScrolling && !skipFix) {
    parent.style.overflow = 'initial';
    return scrollDoc();
  }
  return parent;
}
function hasCustomScrollParent(element, skipFix) {
  if (!element) return false;
  const parent = getScrollParent(element, skipFix);
  return !parent.isSameNode(scrollDoc());
}
function hasCustomOffsetParent(element) {
  return element.offsetParent !== document.body;
}
function hasPosition(el) {
  let type = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 'fixed';
  if (!el || !(el instanceof HTMLElement)) {
    return false;
  }
  const {
    nodeName
  } = el;
  if (nodeName === 'BODY' || nodeName === 'HTML') {
    return false;
  }
  const computedStyles = getStyleComputedProperty(el);
  if (computedStyles && computedStyles.position === type) {
    return true;
  }
  return hasPosition(el.parentNode, type);
}
function isElementVisible(element) {
  if (!element) return false;
  let parentElement = element;
  while (parentElement) {
    if (parentElement === document.body) break;
    if (parentElement instanceof HTMLElement) {
      const {
        display,
        visibility
      } = getComputedStyle(parentElement);
      if (display === 'none' || visibility === 'hidden') {
        return false;
      }
    }
    parentElement = parentElement.parentNode;
  }
  return true;
}
function getElementPosition(element, offset, skipFix) {
  var _elementRect$top;
  const elementRect = getClientRect(element);
  const parent = getScrollParent(element, skipFix);
  const hasScrollParent = hasCustomScrollParent(element, skipFix);
  let parentTop = 0;
  if (parent instanceof HTMLElement) {
    parentTop = parent.scrollTop;
  }
  const top = ((_elementRect$top = elementRect === null || elementRect === void 0 ? void 0 : elementRect.top) !== null && _elementRect$top !== void 0 ? _elementRect$top : 0) + (!hasScrollParent && !hasPosition(element) ? parentTop : 0);
  return Math.floor(top - offset);
}
function getTopOffset(element) {
  if (element instanceof HTMLElement) {
    if (element.offsetParent instanceof HTMLElement) {
      return getTopOffset(element.offsetParent) + element.offsetTop;
    }
    return element.offsetTop;
  }
  return 0;
}
function getScrollTo(element, offset, skipFix) {
  if (!element) {
    return 0;
  }
  const parent = scrollParent(element);
  let top = getTopOffset(element);
  if (hasCustomScrollParent(element, skipFix) && !hasCustomOffsetParent(element)) {
    top -= getTopOffset(parent);
  }
  return Math.floor(top - offset);
}
function scrollTo(value) {
  let element = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : scrollDoc();
  let scrollDuration = arguments.length > 2 ? arguments[2] : undefined;
  return new Promise((resolve, reject) => {
    const scrollTop = element instanceof Document ? 0 : element.scrollTop;
    const limit = value > scrollTop ? value - scrollTop : scrollTop - value;
    element.customScrolling = true;
    scroll.top(element, value, {
      duration: limit < 100 ? 50 : scrollDuration
    }, error => {
      element.customScrolling = false;
      if (error && error.message !== 'Element already at target scroll position') {
        return reject(error);
      }
      return resolve();
    });
  });
}
function getStyles(isMobile) {
  const width = window.innerWidth > 480 ? 380 : 290;
  const defaultStyles = {
    tooltip: {
      width: isMobile ? '100%' : width
    },
    floaterStyles: {
      options: {
        zIndex: 1021
      },
      floater: {
        width: isMobile ? 'calc(100% - 20px)' : ''
      }
    }
  };
  return defaultStyles;
}
var DEFAULTS = {
  floaterProps: {
    options: {
      preventOverflow: {
        boundariesElement: 'scrollParent'
      }
    },
    wrapperOptions: {
      offset: -18,
      position: true
    }
  },
  step: {
    placement: 'auto',
    offset: 20,
    spotlightPadding: 12
  }
};
const sharedTourProps = ['disableScrolling', 'disableParentScroll', 'floaterProps', 'locale', 'spotlightClicks', 'spotlightPadding'];
const stepProps = [...sharedTourProps, 'disableBeacon', 'offset'];
function getTourProps(props) {
  return Object.keys(props).filter(d => includes(sharedTourProps, d)).reduce((acc, i) => {
    acc[i] = props[i];
    return acc;
  }, {});
}
function getMergedStep(step, props) {
  if (!step) return;
  stepProps.forEach(prop => delete step[String(prop)]);
  const mergedStep = deepmerge.all([getTourProps(props), DEFAULTS.step, step], {
    isMergeableObject: is.plainObject
  });
  const styles = getStyles(props.isMobile);
  const scrollParent = hasCustomScrollParent(getElement(step.target), mergedStep.disableParentScroll || false);
  const floaterProps = deepmerge.all([props.floaterProps || {}, DEFAULTS.floaterProps, mergedStep.floaterProps || {}]);
  floaterProps.offset = mergedStep.offset;
  floaterProps.styles = deepmerge(floaterProps.styles || {}, styles.floaterStyles || {});
  delete styles.floaterStyles;
  floaterProps.offset += props.spotlightPadding || 0;
  if (scrollParent) {
    floaterProps.options.preventOverflow.boundariesElement = 'window';
  }
  return _objectSpread(_objectSpread({}, mergedStep), {}, {
    locale: props.locale,
    floaterProps,
    styles: styles
  });
}
function validateStep(step) {
  if (!is.plainObject(step)) {
    return false;
  }
  if (!step.target) {
    return false;
  }
  return true;
}
function validateSteps(steps) {
  if (!is.array(steps)) {
    return false;
  }
  return steps.every(d => validateStep(d));
}
class Scope {
  constructor(_element) {
    let options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    _defineProperty(this, "element", void 0);
    _defineProperty(this, "options", void 0);
    _defineProperty(this, "canBeTabbed", element => {
      let {
        tabIndex
      } = element || {};
      if (!tabIndex || tabIndex < 0) tabIndex = undefined;
      const isTabIndexNaN = Number.isNaN(tabIndex);
      return !isTabIndexNaN && this.canHaveFocus(element);
    });
    _defineProperty(this, "canHaveFocus", element => {
      const validTabNodes = /input|select|textarea|button|object/;
      const nodeName = element.nodeName.toLowerCase();
      const res = validTabNodes.test(nodeName) && !element.getAttribute('disabled') || nodeName === 'a' && !!element.getAttribute('href');
      return res && this.isVisible(element);
    });
    _defineProperty(this, "findValidTabElements", () => {
      const {
        closeElement,
        primaryElement: _primaryElement,
        isMobile,
        srCloseElement,
        srPrimaryElement
      } = this.options;
      const arrayOfElements = [].slice.call(this.element.querySelectorAll('*'), 0);
      const primaryElement = isMobile ? _primaryElement : undefined;
      return [...arrayOfElements, closeElement, primaryElement].filter(element => element).filter(this.canBeTabbed).filter(element => element !== srCloseElement).filter(element => element !== srPrimaryElement);
    });
    _defineProperty(this, "handleKeyDown", e => {
      const {
        keyCode = 9
      } = this.options;
      if (e.keyCode === keyCode) {
        this.interceptTab(e);
      }
    });
    _defineProperty(this, "interceptTab", event => {
      var _elements$x;
      event.preventDefault();
      const elements = this.findValidTabElements();
      const {
        shiftKey
      } = event;
      if (!elements.length) {
        return;
      }
      let x = document.activeElement ? elements.indexOf(document.activeElement) : 0;
      if (x === -1 || !shiftKey && x + 1 === elements.length) {
        x = 0;
      } else if (shiftKey && x === 0) {
        x = elements.length - 1;
      } else {
        x += shiftKey ? -1 : 1;
      }
      (_elements$x = elements[x]) === null || _elements$x === void 0 || _elements$x.focus();
    });
    _defineProperty(this, "isHidden", element => {
      const noSize = element.offsetWidth <= 0 && element.offsetHeight <= 0;
      const style = window.getComputedStyle(element);
      if (noSize && !element.innerHTML) return true;
      return noSize && style.getPropertyValue('overflow') !== 'visible' || style.getPropertyValue('display') === 'none';
    });
    _defineProperty(this, "isVisible", element => {
      let parentElement = element;
      while (parentElement) {
        if (parentElement instanceof HTMLElement) {
          if (parentElement === document.body) break;
          if (this.isHidden(parentElement)) return false;
          parentElement = parentElement.parentNode;
        }
      }
      return true;
    });
    _defineProperty(this, "removeScope", () => {
      window.removeEventListener('keydown', this.handleKeyDown);
    });
    _defineProperty(this, "checkFocus", target => {
      if (document.activeElement !== target) {
        target.focus();
        window.requestAnimationFrame(() => this.checkFocus(target));
      }
    });
    _defineProperty(this, "setFocus", () => {
      if (this.element) {
        window.requestAnimationFrame(() => this.checkFocus(this.element));
      }
    });
    if (!(_element instanceof HTMLElement)) {
      throw new TypeError('Invalid parameter: element must be an HTMLElement');
    }
    this.element = _element;
    this.options = options;
    window.addEventListener('keydown', this.handleKeyDown, false);
    this.setFocus();
  }
}
const mobilePlacements = ['top', 'bottom', 'auto', 'center'];
const namespace$3 = 'andes-coach-marks__overlay';
class CoachMarkOverlay extends React.Component {
  constructor(props) {
    super(props);
    _defineProperty(this, "handleMouseMove", event => {
      const {
        mouseOverSpotlight
      } = this.state;
      const {
        spotlightStyles
      } = this.props;
      const {
        height,
        left,
        position,
        top,
        width
      } = spotlightStyles;
      const offsetY = position === 'fixed' ? event.clientY : event.pageY;
      const offsetX = position === 'fixed' ? event.clientX : event.pageX;
      const inSpotlightHeight = offsetY >= top && offsetY <= top + height;
      const inSpotlightWidth = offsetX >= left && offsetX <= left + width;
      const inSpotlight = inSpotlightWidth && inSpotlightHeight;
      if (inSpotlight !== mouseOverSpotlight) {
        this.updateState({
          mouseOverSpotlight: inSpotlight
        });
      }
    });
    _defineProperty(this, "handleScroll", () => {
      const {
        target
      } = this.props;
      const element = getElement(target);
      if (this.scrollParent !== document) {
        const {
          isScrolling
        } = this.state;
        if (!isScrolling) {
          this.updateState({
            isScrolling: true,
            showSpotlight: false
          });
        }
        clearTimeout(this.scrollTimeout);
        this.scrollTimeout = setTimeout(() => {
          this.updateState({
            isScrolling: false,
            showSpotlight: true
          });
        }, 50);
      } else if (hasPosition(element, 'sticky')) {
        this.updateState({});
      }
    });
    _defineProperty(this, "handleResize", () => {
      clearTimeout(this.resizeTimeout);
      this.resizeTimeout = setTimeout(() => {
        if (!this._isMounted) {
          return;
        }
        this.forceUpdate();
      }, 100);
    });
    this.state = {
      mouseOverSpotlight: false,
      isScrolling: false,
      showSpotlight: true
    };
    this._isMounted = false;
  }
  componentDidMount() {
    const {
      disableParentScroll,
      target
    } = this.props;
    const element = getElement(target);
    this.scrollParent = getScrollParent(element, disableParentScroll, true);
    this._isMounted = true;
    window.addEventListener('resize', this.handleResize);
  }
  componentDidUpdate(prevProps) {
    const {
      lifecycle,
      spotlightClicks
    } = this.props;
    const {
      changed
    } = treeChanges(prevProps, this.props);
    if (changed('lifecycle', LIFECYCLE$1.TOOLTIP)) {
      var _this$scrollParent;
      (_this$scrollParent = this.scrollParent) === null || _this$scrollParent === void 0 || _this$scrollParent.addEventListener('scroll', this.handleScroll, {
        passive: true
      });
      setTimeout(() => {
        const {
          isScrolling
        } = this.state;
        if (!isScrolling) {
          this.updateState({
            showSpotlight: true
          });
        }
      }, 100);
    }
    if (changed('spotlightClicks') || changed('lifecycle')) {
      if (spotlightClicks && lifecycle === LIFECYCLE$1.TOOLTIP) {
        window.addEventListener('mousemove', this.handleMouseMove, false);
      } else if (lifecycle !== LIFECYCLE$1.TOOLTIP) {
        window.removeEventListener('mousemove', this.handleMouseMove);
      }
    }
  }
  componentWillUnmount() {
    var _this$scrollParent2;
    this._isMounted = false;
    window.removeEventListener('mousemove', this.handleMouseMove);
    window.removeEventListener('resize', this.handleResize);
    clearTimeout(this.resizeTimeout);
    clearTimeout(this.scrollTimeout);
    (_this$scrollParent2 = this.scrollParent) === null || _this$scrollParent2 === void 0 || _this$scrollParent2.removeEventListener('scroll', this.handleScroll);
  }
  updateState(state) {
    if (!this._isMounted) {
      return;
    }
    this.setState(prevState => _objectSpread(_objectSpread({}, prevState), state));
  }
  render() {
    const {
      mouseOverSpotlight,
      showSpotlight
    } = this.state;
    const {
      id,
      lifecycle,
      placement,
      spotlightStyles
    } = this.props;
    if (lifecycle !== LIFECYCLE$1.TOOLTIP) {
      return null;
    }
    const stylesOverlay = {
      height: getDocumentHeight(),
      pointerEvents: mouseOverSpotlight ? 'none' : 'auto'
    };
    return /*#__PURE__*/_jsx("div", {
      id: id,
      className: namespace$3,
      style: stylesOverlay
    }, void 0, placement !== 'center' && showSpotlight && /*#__PURE__*/_jsx("div", {
      className: "".concat(namespace$3, "__spotlight"),
      style: spotlightStyles
    }));
  }
}
var Overlay = CoachMarkOverlay;
const TOP_LEFT = 'top-left';
const TOP_RIGHT = 'top-right';
const BOTTOM_LEFT = 'bottom-left';
const BOTTOM_RIGHT = 'bottom-right';
const namespace$2 = 'andes-coach-marks';
const ArrowBottomLeft = _ref => {
  let {
    p1,
    p2,
    fill
  } = _ref;
  return /*#__PURE__*/_jsx("svg", {
    height: "100%",
    width: "100%",
    xmlns: "http://www.w3.org/2000/svg",
    overflow: "visible"
  }, void 0, /*#__PURE__*/_jsx("path", {
    d: "M ".concat(p1.x, " 0 Q ").concat(p1.x, " 24 ").concat(p1.x - 24, " 24 L ").concat(p2.x + 24, " 24 Q ").concat(p2.x, " 24 ").concat(p2.x, " 48"),
    stroke: fill,
    strokeWidth: "1.5px",
    fill: "none"
  }), /*#__PURE__*/_jsx("path", {
    d: "M ".concat(p2.x, " 48 L ").concat(p2.x - 5, " 43"),
    stroke: fill,
    strokeWidth: "1.5px",
    fill: "none"
  }), /*#__PURE__*/_jsx("path", {
    d: "M ".concat(p2.x, " 48 L ").concat(p2.x + 5, " 43"),
    stroke: fill,
    strokeWidth: "1.5px",
    fill: "none"
  }));
};
var ArrowBottomLeft$1 = ArrowBottomLeft;
const ArrowTopRight = _ref => {
  let {
    p1,
    p2,
    fill
  } = _ref;
  return /*#__PURE__*/_jsx("svg", {
    height: "100%",
    width: "100%",
    xmlns: "http://www.w3.org/2000/svg",
    overflow: "visible"
  }, void 0, /*#__PURE__*/_jsx("path", {
    d: "M ".concat(p1.x, " 48 Q ").concat(p1.x, " 24 ").concat(p1.x + 24, " 24 L ").concat(p2.x - 24, " 24 Q ").concat(p2.x, " 24 ").concat(p2.x, " 0"),
    stroke: fill,
    strokeWidth: "1.5px",
    fill: "none"
  }), /*#__PURE__*/_jsx("path", {
    d: "M ".concat(p2.x, " 0 L ").concat(p2.x - 5, " ", 5),
    stroke: fill,
    strokeWidth: "1.5px",
    fill: "none"
  }), /*#__PURE__*/_jsx("path", {
    d: "M ".concat(p2.x, " 0 L ").concat(p2.x + 5, " ", 5),
    stroke: fill,
    strokeWidth: "1.5px",
    fill: "none"
  }));
};
var ArrowTopRight$1 = ArrowTopRight;
const ArrowTopLeft = _ref => {
  let {
    p1,
    p2,
    fill
  } = _ref;
  return /*#__PURE__*/_jsx("svg", {
    height: "100%",
    width: "100%",
    xmlns: "http://www.w3.org/2000/svg",
    overflow: "visible"
  }, void 0, /*#__PURE__*/_jsx("path", {
    d: "M ".concat(p1.x, " 48 Q ").concat(p1.x, " 24 ").concat(p1.x - 24, " 24 L ").concat(p2.x + 24, " 24 Q ").concat(p2.x, " 24 ").concat(p2.x, " 0"),
    stroke: fill,
    strokeWidth: "1.5px",
    fill: "none"
  }), /*#__PURE__*/_jsx("path", {
    d: "M ".concat(p2.x, " 0 L ").concat(p2.x - 5, " ", 5),
    stroke: fill,
    strokeWidth: "1.5px",
    fill: "none"
  }), /*#__PURE__*/_jsx("path", {
    d: "M ".concat(p2.x, " 0 L ").concat(p2.x + 5, " ", 5),
    stroke: fill,
    strokeWidth: "1.5px",
    fill: "none"
  }));
};
var ArrowTopLeft$1 = ArrowTopLeft;
const ArrowBottomRight = _ref => {
  let {
    p1,
    p2,
    fill
  } = _ref;
  return /*#__PURE__*/_jsx("svg", {
    height: "100%",
    width: "100%",
    xmlns: "http://www.w3.org/2000/svg",
    overflow: "visible"
  }, void 0, /*#__PURE__*/_jsx("path", {
    d: "M ".concat(p1.x, " 0 Q ").concat(p1.x, " 24 ").concat(p1.x + 24, " 24 L ").concat(p2.x - 24, " 24 Q ").concat(p2.x, " 24 ").concat(p2.x, " 48"),
    stroke: fill,
    strokeWidth: "1.5px",
    fill: "none"
  }), /*#__PURE__*/_jsx("path", {
    d: "M ".concat(p2.x, " 48 L ").concat(p2.x - 5, " 43"),
    stroke: fill,
    strokeWidth: "1.5px",
    fill: "none"
  }), /*#__PURE__*/_jsx("path", {
    d: "M ".concat(p2.x, " 48 L ").concat(p2.x + 5, " 43"),
    stroke: fill,
    strokeWidth: "1.5px",
    fill: "none"
  }));
};
var ArrowBottomRight$1 = ArrowBottomRight;
const _excluded = ["position", "fill"];
const ArrowIcon = /*#__PURE__*/forwardRef((props, ref) => {
  const {
      position,
      fill = '#fff'
    } = props,
    rest = _objectWithoutProperties(props, _excluded);
  const Icon = () => {
    switch (position) {
      case TOP_LEFT:
        return /*#__PURE__*/React.createElement(ArrowTopLeft$1, _extends({}, rest, {
          fill: fill
        }));
      case TOP_RIGHT:
        return /*#__PURE__*/React.createElement(ArrowTopRight$1, _extends({}, rest, {
          fill: fill
        }));
      case BOTTOM_LEFT:
        return /*#__PURE__*/React.createElement(ArrowBottomLeft$1, _extends({}, rest, {
          fill: fill
        }));
      case BOTTOM_RIGHT:
        return /*#__PURE__*/React.createElement(ArrowBottomRight$1, _extends({}, rest, {
          fill: fill
        }));
    }
  };
  return /*#__PURE__*/React.createElement("div", {
    className: "".concat(namespace$2, "__arrow-icon ").concat(namespace$2, "__arrow-icon-").concat(position),
    ref: ref
  }, /*#__PURE__*/_jsx(Icon, {}));
});
var ArrowIcon$1 = ArrowIcon;
const Pagination = _ref => {
  let {
    i18n,
    id,
    namespace,
    page,
    totalPages,
    className
  } = _ref;
  return /*#__PURE__*/_jsx("div", {
    className: classNames("".concat(namespace, "__progress"), className),
    id: id
  }, void 0, /*#__PURE__*/_jsx("span", {}, void 0, page + 1, " ", i18n.pgettext(namespace$2, 'de'), " ", totalPages));
};
var Pagination$1 = Pagination;
const TOP = 'top';
const BOTTOM = 'bottom';
const LEFT = 'left';
const RIGHT = 'right';
const TOOLTIP_MARGIN = 10;
const CENTERED_MARGIN = 48;
const ARROW_START_MARGIN = 8;
function getOffset(el) {
  const rect = el.getBoundingClientRect();
  return {
    left: rect.left,
    top: rect.top,
    bottom: rect.bottom,
    right: rect.right,
    x: rect.x,
    y: rect.y
  };
}
const positionArrowAlongTooltip = _ref => {
  let {
    arrowRef,
    tooltipRef
  } = _ref;
  const {
    left
  } = tooltipRef.current.getBoundingClientRect();
  const marginLeft = "-".concat(left - TOOLTIP_MARGIN, "px");
  if (marginLeft !== '-0px') {
    tooltipRef.current.style.left = marginLeft;
  }
  if (arrowRef.current) {
    arrowRef.current.style.left = tooltipRef.current.style.left;
  }
};
const getArrowPositions = _ref2 => {
  let {
    spotlightRef,
    tooltipRef,
    setMeasures
  } = _ref2;
  const spotlight = spotlightRef;
  const tooltip = tooltipRef.current;
  if (tooltip && spotlight) {
    const tooltipOffset = getOffset(tooltip);
    const spotlightOffset = getOffset(spotlight);
    const verticalPosition = tooltipOffset.bottom > spotlightOffset.bottom ? TOP : BOTTOM;
    const spotlightMeasures = {
      x: spotlightOffset.left + spotlight.clientWidth / 2,
      y: verticalPosition === BOTTOM ? spotlightOffset.bottom : spotlightOffset.top - ARROW_START_MARGIN
    };
    const tooltipMeasures = {
      x: tooltipOffset.left + tooltip.clientWidth / 2,
      y: verticalPosition === BOTTOM ? tooltipOffset.top + ARROW_START_MARGIN : spotlightOffset.bottom
    };
    if (tooltipMeasures && spotlightMeasures) {
      const xDiff = spotlightMeasures.x - tooltipMeasures.x;
      const isAlmostCentered = Math.abs(xDiff) < CENTERED_MARGIN;
      if (!isAlmostCentered) {
        let position = verticalPosition;
        position += xDiff > 0 ? "-".concat(RIGHT) : "-".concat(LEFT);
        setMeasures({
          tooltipMeasures,
          spotlightMeasures,
          position
        });
        return;
      }
      setMeasures(undefined);
    }
  }
};
const namespace$1 = "".concat(namespace$2, "__tooltip");
const CoachMarkTooltip = _ref => {
  let {
    continuous,
    id,
    index,
    isMobile = false,
    size,
    step,
    setTooltipRef,
    helpers,
    primaryButton,
    spotlightRef,
    i18n,
    srCloseButton,
    srPrimaryButton,
    srPagination
  } = _ref;
  const [measures, setMeasures] = useState(undefined);
  const tooltipRef = useRef(null);
  const arrowRef = useRef(null);
  const timerRef = useRef();
  usePreventScroll();
  useEffect(() => {
    if (isMobile && measures) {
      positionArrowAlongTooltip({
        arrowRef,
        tooltipRef: tooltipRef
      });
    }
  }, [measures, isMobile]);
  useEffect(() => {
    if (isMobile) {
      getArrowPositions({
        spotlightRef,
        tooltipRef,
        setMeasures
      });
    }
  }, [isMobile, spotlightRef]);
  const onScroll = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
    }
    timerRef.current = setTimeout(() => {
      getArrowPositions({
        spotlightRef,
        tooltipRef,
        setMeasures
      });
    }, 100);
  }, [spotlightRef, tooltipRef, setMeasures]);
  useEffect(() => {
    if (isMobile) {
      window.addEventListener('scroll', onScroll);
    }
    return () => document.removeEventListener('scroll', onScroll);
  }, [isMobile, spotlightRef, onScroll]);
  const setLocalTooltipRef = ref => {
    tooltipRef.current = ref;
    setTooltipRef(ref);
  };
  const {
    content,
    title,
    styles
  } = step;
  const {
    back
  } = step.locale;
  const backButton = () => {
    if (index > 0) {
      return /*#__PURE__*/_jsx(Button, {
        className: "".concat(namespace$1, "__back-button"),
        id: "".concat(id, "-back-button"),
        size: "medium",
        onClick: helpers.prev,
        "data-action": "back"
      }, void 0, back);
    }
  };
  const renderArrow = () => measures && isMobile && /*#__PURE__*/React.createElement(ArrowIcon$1, {
    p1: measures.tooltipMeasures,
    p2: measures.spotlightMeasures,
    position: measures.position,
    ref: arrowRef
  });
  return /*#__PURE__*/React.createElement(React.Fragment, null, measures && includes(measures.position, 'top') && renderArrow(), /*#__PURE__*/React.createElement("div", {
    className: namespace$1,
    style: styles.tooltip,
    "aria-modal": true,
    ref: setLocalTooltipRef,
    role: "alertdialog",
    tabIndex: -1
  }, /*#__PURE__*/_jsx("div", {
    className: "".concat(namespace$1, "__content")
  }, void 0, title && /*#__PURE__*/_jsx("div", {
    className: "".concat(namespace$1, "__content-title")
  }, void 0, title), content && /*#__PURE__*/_jsx("div", {
    className: "".concat(namespace$1, "__content-text")
  }, void 0, content)), /*#__PURE__*/_jsx("div", {
    className: "".concat(namespace$1, "__footer")
  }, void 0, srPagination && srPagination(), !isMobile && /*#__PURE__*/React.createElement(React.Fragment, null, continuous && /*#__PURE__*/_jsx(Pagination$1, {
    id: "".concat(id, "-pagination"),
    page: index,
    totalPages: size,
    namespace: namespace$1,
    i18n: i18n
  }), backButton()), primaryButton && primaryButton(), srPrimaryButton && srPrimaryButton(), srCloseButton && srCloseButton())), measures && includes(measures.position, 'bottom') && renderArrow());
};
var Tooltip = CoachMarkTooltip;
var _Close, _div;
const namespace = "".concat(namespace$2, "__step");
class CoachMarkStep extends React.Component {
  constructor(props) {
    super(props);
    _defineProperty(this, "scope", {
      removeScope: () => {}
    });
    _defineProperty(this, "handleClickCloseButton", () => {
      const {
        helpers
      } = this.props;
      helpers.skip();
    });
    _defineProperty(this, "setCloseButtonRef", ref => {
      this.closeButtonRef = ref;
    });
    _defineProperty(this, "setTooltipRef", ref => {
      this.tooltip = ref;
    });
    _defineProperty(this, "setSpotlightRef", ref => {
      this.spotlightRef = ref;
    });
    _defineProperty(this, "setPopper", (popper, type) => {
      const {
        action,
        setPopper,
        update
      } = this.props;
      if (type === 'wrapper') {
        this.beaconPopper = popper;
      } else {
        this.tooltipPopper = popper;
      }
      setPopper(popper, type);
      if (this.beaconPopper && this.tooltipPopper) {
        update({
          action: action === ACTIONS$1.CLOSE ? ACTIONS$1.CLOSE : action,
          lifecycle: LIFECYCLE$1.READY
        });
      }
    });
    _defineProperty(this, "setPrimaryRef", ref => {
      this.primaryRef = ref;
    });
    _defineProperty(this, "setSrCloseButtonRef", ref => {
      this.srCloseButtonRef = ref;
    });
    _defineProperty(this, "setSrPrimaryRef", ref => {
      this.srPrimaryRef = ref;
    });
    _defineProperty(this, "getPlacement", () => {
      const {
        step: {
          placement
        },
        isMobile
      } = this.props;
      if (isMobile) {
        return includes(mobilePlacements, placement) ? placement : 'auto';
      }
      return placement;
    });
    _defineProperty(this, "handlePrimaryClick", () => {
      const {
        continuous,
        helpers
      } = this.props;
      if (!continuous) {
        helpers.close();
        return;
      }
      helpers.next();
    });
    _defineProperty(this, "renderPrimaryButton", () => {
      const {
        continuous,
        step,
        id,
        index,
        size,
        isMobile
      } = this.props;
      const {
        next,
        last
      } = step.locale;
      let primaryText = continuous ? next : '';
      if (index + 1 === size) {
        primaryText = last;
      }
      return /*#__PURE__*/React.createElement(Button, {
        className: "".concat(namespace, "__next-button"),
        size: isMobile ? 'large' : 'medium',
        onClick: this.handlePrimaryClick,
        "data-action": "primary",
        fullWidth: isMobile,
        id: "".concat(id, "-next-button"),
        ref: this.setPrimaryRef
      }, primaryText);
    });
    _defineProperty(this, "renderSrPrimaryButton", () => {
      const {
        continuous,
        step,
        index,
        size
      } = this.props;
      const {
        next,
        last
      } = step.locale;
      let primaryText = continuous ? next : '';
      if (index + 1 === size) {
        primaryText = last;
      }
      return /*#__PURE__*/React.createElement("button", {
        className: "andes-visually-hidden",
        onClick: this.handlePrimaryClick,
        ref: this.setSrPrimaryRef,
        tabIndex: -1,
        type: "button"
      }, primaryText);
    });
    _defineProperty(this, "renderSrCloseButton", () => {
      const {
        closeButtonSrLabel
      } = this.props;
      return /*#__PURE__*/React.createElement("button", {
        className: "andes-visually-hidden",
        ref: this.setSrCloseButtonRef,
        tabIndex: -1,
        type: "button",
        onClick: this.handleClickCloseButton
      }, closeButtonSrLabel);
    });
    _defineProperty(this, "renderSrPagination", () => {
      const {
        isMobile,
        continuous,
        step,
        index,
        size,
        i18n,
        id
      } = this.props;
      const {
        hidePagination
      } = step;
      if (isMobile && continuous && !hidePagination) {
        return /*#__PURE__*/_jsx(Pagination$1, {
          className: "andes-visually-hidden",
          page: index,
          totalPages: size,
          namespace: namespace,
          i18n: i18n,
          id: "".concat(id, "-pagination")
        });
      }
    });
    this.setInnerRef = this.setInnerRef.bind(this);
  }
  componentDidUpdate(prevProps) {
    const {
      action,
      onChange,
      controlled,
      index,
      lifecycle,
      size,
      status,
      step,
      update,
      isMobile
    } = this.props;
    const {
      changed,
      changedFrom
    } = treeChanges(prevProps, this.props);
    const state = {
      action,
      controlled,
      index,
      lifecycle,
      size,
      status
    };
    const hasStoreChanged = changed('action') || changed('index') || changed('lifecycle') || changed('status');
    const hasStarted = changedFrom('lifecycle', [LIFECYCLE$1.TOOLTIP, LIFECYCLE$1.INIT], LIFECYCLE$1.INIT);
    const isAfterAction = changed('action', [ACTIONS$1.NEXT, ACTIONS$1.PREV, ACTIONS$1.SKIP, ACTIONS$1.CLOSE]);
    if (isAfterAction && (hasStarted || controlled)) {
      onChange(_objectSpread(_objectSpread({}, state), {}, {
        index: prevProps.index,
        lifecycle: LIFECYCLE$1.COMPLETE,
        step: prevProps.step,
        type: EVENTS$1.STEP_AFTER
      }));
    }
    if (hasStoreChanged && step) {
      const element = getElement(step.target);
      const elementExists = !!element;
      const hasRenderedTarget = elementExists && isElementVisible(element);
      if (hasRenderedTarget) {
        if (changedFrom('status', STATUS$1.READY, STATUS$1.RUNNING) || changedFrom('lifecycle', LIFECYCLE$1.INIT, LIFECYCLE$1.READY)) {
          onChange(_objectSpread(_objectSpread({}, state), {}, {
            step,
            type: EVENTS$1.STEP_BEFORE
          }));
        }
      } else {
        onChange(_objectSpread(_objectSpread({}, state), {}, {
          type: EVENTS$1.TARGET_NOT_FOUND,
          step
        }));
        if (!controlled) {
          update({
            index: index + (includes([ACTIONS$1.PREV], action) ? -1 : 1)
          });
        }
      }
    }
    if (changedFrom('lifecycle', LIFECYCLE$1.INIT, LIFECYCLE$1.READY)) {
      update({
        lifecycle: LIFECYCLE$1.TOOLTIP
      });
    }
    if (changed('lifecycle', LIFECYCLE$1.BEACON)) {
      onChange(_objectSpread(_objectSpread({}, state), {}, {
        step,
        type: EVENTS$1.BEACON
      }));
    }
    if (changed('lifecycle', LIFECYCLE$1.TOOLTIP)) {
      onChange(_objectSpread(_objectSpread({}, state), {}, {
        step,
        type: EVENTS$1.TOOLTIP
      }));
      this.scope = new Scope(this.tooltip, {
        selector: '[data-action=primary]',
        closeElement: this.closeButtonRef,
        primaryElement: this.primaryRef,
        srCloseElement: this.srCloseButtonRef,
        srPrimaryElement: this.srPrimaryRef,
        isMobile
      });
      this.scope.setFocus();
    }
    if (changedFrom('lifecycle', [LIFECYCLE$1.TOOLTIP, LIFECYCLE$1.INIT], LIFECYCLE$1.INIT)) {
      this.scope.removeScope();
      delete this.beaconPopper;
      delete this.tooltipPopper;
    }
  }
  componentWillUnmount() {
    this.scope.removeScope();
  }
  get open() {
    const {
      lifecycle
    } = this.props;
    return lifecycle === LIFECYCLE$1.TOOLTIP;
  }
  get spotlightStyles() {
    const {
      step
    } = this.props;
    const {
      disableParentScroll = false,
      spotlightClicks,
      spotlightPadding = 10,
      target
    } = step;
    const element = getElement(target);
    const elementRect = getClientRect(element);
    const isFixedTarget = hasPosition(element);
    const top = getElementPosition(element, spotlightPadding, disableParentScroll);
    return {
      height: elementRect !== null && elementRect !== void 0 && elementRect.height ? Math.round(elementRect.height + spotlightPadding * 2) : 0,
      left: elementRect !== null && elementRect !== void 0 && elementRect.left ? Math.round(elementRect.left - spotlightPadding) : 0,
      pointerEvents: spotlightClicks ? 'none' : 'auto',
      position: isFixedTarget ? 'fixed' : 'absolute',
      top,
      width: elementRect !== null && elementRect !== void 0 && elementRect.width ? Math.round(elementRect.width + spotlightPadding * 2) : 0
    };
  }
  setInnerRef(ref) {
    const {
      innerRef
    } = this.props;
    if (innerRef) {
      if (typeof innerRef === 'function') {
        innerRef(ref);
      } else {
        innerRef.current = ref;
      }
    }
  }
  render() {
    const {
      continuous,
      closeButtonSrLabel,
      helpers,
      id,
      index,
      isMobile,
      lifecycle,
      size,
      step,
      i18n
    } = this.props;
    const {
      showNextButtonWithContent,
      floaterProps,
      hidePagination,
      hideCloseButton,
      disableParentScroll = false
    } = step;
    const target = getElement(step.target);
    if (!validateStep(step) || !is.domElement(target)) {
      return null;
    }
    const placement = this.getPlacement();
    return /*#__PURE__*/React.createElement("div", {
      key: "".concat(namespace, "__step-").concat(index),
      className: namespace,
      id: id,
      ref: this.setInnerRef
    }, /*#__PURE__*/React.createElement(Overlay, _extends({}, step, {
      disableParentScroll: disableParentScroll,
      id: "".concat(id, "-overlay"),
      spotlightStyles: this.spotlightStyles,
      lifecycle: lifecycle
    })), isMobile && continuous && !hidePagination && /*#__PURE__*/_jsx(Pagination$1, {
      id: "".concat(id, "-pagination"),
      page: index,
      totalPages: size,
      namespace: namespace,
      i18n: i18n
    }), !hideCloseButton && /*#__PURE__*/React.createElement("button", {
      "aria-label": closeButtonSrLabel || i18n.pgettext(namespace$2, 'close'),
      className: "".concat(namespace, "__close-button"),
      onClick: this.handleClickCloseButton,
      ref: this.setCloseButtonRef,
      id: "".concat(id, "-close-button"),
      type: "button"
    }, _Close || (_Close = /*#__PURE__*/_jsx(Close20, {
      color: "white"
    }))), placement !== 'center' && /*#__PURE__*/React.createElement("div", {
      className: "andes-coach-marks__spotlight-border",
      style: this.spotlightStyles,
      ref: this.setSpotlightRef
    }), /*#__PURE__*/React.createElement(Floater, _extends({
      component: () => /*#__PURE__*/_jsx(Tooltip, {
        continuous: continuous,
        helpers: helpers,
        id: "".concat(id, "-tooltip"),
        index: index,
        i18n: i18n,
        setTooltipRef: this.setTooltipRef,
        size: size,
        step: step,
        isMobile: isMobile,
        spotlightRef: this.spotlightRef,
        primaryButton: showNextButtonWithContent || !isMobile ? this.renderPrimaryButton : undefined,
        srCloseButton: hideCloseButton ? undefined : this.renderSrCloseButton,
        srPrimaryButton: isMobile && !showNextButtonWithContent ? this.renderSrPrimaryButton : undefined,
        srPagination: this.renderSrPagination
      }),
      getPopper: this.setPopper,
      id: "".concat(namespace, "__step-").concat(index),
      open: this.open,
      placement: placement,
      target: target,
      hideArrow: isMobile,
      disableFlip: isMobile
    }, floaterProps), _div || (_div = /*#__PURE__*/_jsx("div", {}))), isMobile && !showNextButtonWithContent && this.renderPrimaryButton());
  }
}
var Step = CoachMarkStep;
class CoachMarks extends React.Component {
  constructor(props) {
    super(props);
    _defineProperty(this, "initStore", () => {
      const {
        run = true,
        step,
        stepIndex: _willdeprecate$stepIndex
      } = this.props;
      const computedStep = step || _willdeprecate$stepIndex;
      return createStore(_objectSpread(_objectSpread({}, this.props), {}, {
        run,
        controlled: run && is.number(computedStep)
      }));
    });
    _defineProperty(this, "initState", () => {
      const {
        getHelpers
      } = this.props;
      const {
        addListener
      } = this.store;
      addListener(this.syncState);
      if (typeof getHelpers === 'function') getHelpers(this.helpers);
      return this.store.getState();
    });
    _defineProperty(this, "handleChange", data => {
      const {
        callback: _willdeprecate$callback,
        onChange
      } = this.props;
      if (is.function(_willdeprecate$callback)) {
        _willdeprecate$callback(data);
      }
      if (typeof onChange === 'function') onChange(data);
    });
    _defineProperty(this, "handleKeyboard", e => {
      const {
        lifecycle
      } = this.state;
      const intKey = window.Event ? e.which : e.keyCode;
      if (lifecycle === LIFECYCLE$1.TOOLTIP) {
        if (intKey === 27) {
          this.store.skip();
        }
      }
    });
    _defineProperty(this, "syncState", state => {
      this.setState(state);
    });
    _defineProperty(this, "setPopper", (popper, type) => {
      if (type === 'wrapper') {
        this.beaconPopper = popper;
      } else {
        this.tooltipPopper = popper;
      }
    });
    _defineProperty(this, "shouldScroll", (disableScrolling, index, scrollToFirstStep, lifecycle, step, target, prevState) => !disableScrolling && (index !== 0 || scrollToFirstStep || lifecycle === LIFECYCLE$1.TOOLTIP) && step.placement !== 'center' && (!step.isFixed || !hasPosition(target)) && prevState.lifecycle !== lifecycle && includes([LIFECYCLE$1.BEACON, LIFECYCLE$1.TOOLTIP], lifecycle));
    _defineProperty(this, "handleUpdate", state => {
      this.store.update(state);
      if ((state === null || state === void 0 ? void 0 : state.lifecycle) === LIFECYCLE$1.TOOLTIP) {
        const {
          isMobile = false
        } = this.props;
        const {
          index
        } = this.state;
        const {
          bodyElements,
          portalElements
        } = getElementsBySelector("#".concat(namespace$2, "__step__step-").concat(index));
        const elementWithoutOverlay = bodyElements.filter(element => !element.classList.contains('andes-coach-marks'));
        this.setState({
          outsideElements: isMobile ? bodyElements : elementWithoutOverlay,
          portalElements
        });
      }
    });
    _defineProperty(this, "onPortalMount", () => {
      const {
        status,
        lifecycle
      } = this.state;
      if (status === STATUS$1.RUNNING && lifecycle === LIFECYCLE$1.READY) {
        this.handleUpdate({
          lifecycle: LIFECYCLE$1.TOOLTIP
        });
      }
    });
    this.store = this.initStore();
    this.helpers = this.store.getHelpers();
    this.state = this.initState();
  }
  componentDidMount() {
    if (!canUseDOM) return;
    const {
      run = true,
      steps = [],
      isMobile = false
    } = this.props;
    const {
      start
    } = this.store;
    const {
      index
    } = this.state;
    if (validateSteps(steps) && run) {
      start();
    }
    document.body.addEventListener('keydown', this.handleKeyboard, {
      passive: true
    });
    const {
      bodyElements,
      portalElements
    } = getElementsBySelector("#".concat(namespace$2, "__step__step-").concat(index));
    const elementWithoutOverlay = bodyElements.filter(element => !element.classList.contains(namespace$2));
    this.setState({
      outsideElements: isMobile ? bodyElements : elementWithoutOverlay,
      portalElements
    });
  }
  componentDidUpdate(prevProps, prevState) {
    if (!canUseDOM) return;
    const {
      action,
      controlled,
      index,
      lifecycle,
      status,
      outsideElements,
      portalElements
    } = this.state;
    const {
      run = true,
      stepIndex: _willdeprecate$stepIndex,
      step: stepProp,
      steps = []
    } = this.props;
    const {
      steps: prevSteps,
      stepIndex: _willdeprecate$prevStepIndex,
      step: prevStepProp
    } = prevProps;
    const {
      setSteps,
      reset,
      start,
      stop,
      update
    } = this.store;
    const {
      changed: changedProps
    } = treeChanges(prevProps, this.props);
    const {
      changed,
      changedFrom
    } = treeChanges(prevState, this.state);
    const step = getMergedStep(steps[index], this.props);
    const computedStep = stepProp || _willdeprecate$stepIndex || 0;
    const computedPrevStep = prevStepProp || _willdeprecate$prevStepIndex || 0;
    const stepsChanged = !isEqual(prevSteps, steps);
    const stepIndexChanged = is.number(computedStep) && (changedProps('stepIndex') || changedProps('step'));
    const target = getElement(step === null || step === void 0 ? void 0 : step.target);
    if ((prevState === null || prevState === void 0 ? void 0 : prevState.lifecycle) !== lifecycle) {
      const arrayOfOutsideElements = Array.isArray(outsideElements) ? outsideElements : [];
      const arrayOfPortalElements = Array.isArray(portalElements) ? portalElements : [];
      if (lifecycle === LIFECYCLE$1.TOOLTIP) {
        addAriaHidden({
          outsideElements: arrayOfOutsideElements,
          portalElements: arrayOfPortalElements
        });
      } else {
        removeAriaHidden({
          outsideElements: arrayOfOutsideElements
        });
      }
    }
    if (stepsChanged) {
      if (validateSteps(steps)) {
        setSteps(steps);
      } else {
        console.warn('Steps are not valid', steps);
      }
    }
    if (changedProps('run')) {
      if (run) {
        start(computedStep);
      } else {
        stop();
      }
    }
    if (stepIndexChanged) {
      let nextAction = computedPrevStep < computedStep ? ACTIONS$1.NEXT : ACTIONS$1.PREV;
      if (action === ACTIONS$1.STOP) {
        nextAction = ACTIONS$1.START;
      }
      if (!includes([STATUS$1.FINISHED, STATUS$1.SKIPPED], status)) {
        update({
          action: action === ACTIONS$1.CLOSE ? ACTIONS$1.CLOSE : nextAction,
          index: computedStep,
          lifecycle: LIFECYCLE$1.INIT
        });
      }
    }
    if (!controlled && status === STATUS$1.RUNNING && index === 0 && !target) {
      this.store.update({
        index: index + 1
      });
      this.handleChange(_objectSpread(_objectSpread({}, this.state), {}, {
        type: EVENTS$1.TARGET_NOT_FOUND,
        step
      }));
    }
    const callbackData = _objectSpread(_objectSpread({}, this.state), {}, {
      index,
      step
    });
    const isAfterAction = changed('action', [ACTIONS$1.NEXT, ACTIONS$1.PREV, ACTIONS$1.SKIP, ACTIONS$1.CLOSE]);
    if (isAfterAction && changed('status', STATUS$1.PAUSED)) {
      const prevStep = getMergedStep(steps[prevState.index], this.props);
      this.handleChange(_objectSpread(_objectSpread({}, callbackData), {}, {
        index: prevState.index,
        lifecycle: LIFECYCLE$1.COMPLETE,
        step: prevStep,
        type: EVENTS$1.STEP_AFTER
      }));
    }
    if (changed('status', [STATUS$1.FINISHED, STATUS$1.SKIPPED])) {
      const prevStep = getMergedStep(steps[prevState.index], this.props);
      if (!controlled) {
        this.handleChange(_objectSpread(_objectSpread({}, callbackData), {}, {
          index: prevState.index,
          lifecycle: LIFECYCLE$1.COMPLETE,
          step: prevStep,
          type: EVENTS$1.STEP_AFTER
        }));
      }
      if (this.store) this.store.enableWebviewTopbar();
      this.handleChange(_objectSpread(_objectSpread({}, callbackData), {}, {
        type: EVENTS$1.TOUR_END,
        step: prevStep,
        index: prevState.index
      }));
      reset();
    } else if (changedFrom('status', [STATUS$1.IDLE, STATUS$1.READY], STATUS$1.RUNNING)) {
      this.handleChange(_objectSpread(_objectSpread({}, callbackData), {}, {
        type: EVENTS$1.TOUR_START
      }));
    } else if (changed('status')) {
      this.handleChange(_objectSpread(_objectSpread({}, callbackData), {}, {
        type: EVENTS$1.TOUR_STATUS
      }));
    } else if (changed('action', ACTIONS$1.RESET)) {
      this.handleChange(_objectSpread(_objectSpread({}, callbackData), {}, {
        type: EVENTS$1.TOUR_STATUS
      }));
    }
    if (step) {
      this.scrollToStep(prevState);
      if (step.placement === 'center' && status === STATUS$1.RUNNING && lifecycle === LIFECYCLE$1.INIT) {
        this.store.update({
          lifecycle: LIFECYCLE$1.READY
        });
      }
    }
  }
  componentWillUnmount() {
    document.body.removeEventListener('keydown', this.handleKeyboard);
    if (this.store) this.store.enableWebviewTopbar();
    const {
      outsideElements
    } = this.state;
    const arrayOfOutsideElements = Array.isArray(outsideElements) ? outsideElements : [];
    removeAriaHidden({
      outsideElements: arrayOfOutsideElements
    });
  }
  scrollToStep(prevState) {
    const {
      index,
      lifecycle,
      status
    } = this.state;
    const {
      disableScrolling = false,
      disableParentScroll = false,
      disableScrollParentFix: _willdeprecate$disableParentScroll = false,
      scrollToFirstStep = false,
      scrollOffset = 20,
      scrollDuration = 300,
      steps = []
    } = this.props;
    const step = getMergedStep(steps[index], this.props);
    const computedDisableParentScroll = _willdeprecate$disableParentScroll || disableParentScroll;
    if (step) {
      const target = getElement(step.target);
      const shouldScroll = this.shouldScroll(disableScrolling, index, scrollToFirstStep, lifecycle, step, target, prevState);
      if (status === STATUS$1.RUNNING && shouldScroll) {
        const hasCustomScroll = hasCustomScrollParent(target, computedDisableParentScroll);
        const scrollParent = getScrollParent(target, computedDisableParentScroll);
        let scrollY = Math.floor(getScrollTo(target, scrollOffset, computedDisableParentScroll)) || 0;
        if (lifecycle === LIFECYCLE$1.BEACON && this.beaconPopper) {
          const {
            placement,
            popper
          } = this.beaconPopper;
          if (!includes(['bottom'], placement) && !hasCustomScroll) {
            scrollY = Math.floor(popper.top - scrollOffset);
          }
        } else if (lifecycle === LIFECYCLE$1.TOOLTIP && this.tooltipPopper) {
          const {
            flipped,
            placement,
            popper
          } = this.tooltipPopper;
          if (includes(['top', 'right', 'left'], placement) && !flipped && !hasCustomScroll) {
            scrollY = Math.floor(popper.top - scrollOffset);
          } else {
            scrollY -= step.spotlightPadding || 10;
          }
        }
        scrollY = scrollY >= 0 ? scrollY : 0;
        if (status === STATUS$1.RUNNING) {
          scrollTo(scrollY, scrollParent, scrollDuration);
        }
      }
    }
  }
  render() {
    if (!canUseDOM) return null;
    const {
      index,
      status
    } = this.state;
    const {
      closeButtonSrLabel,
      disableScrolling = false,
      id,
      innerRef,
      isMobile = false,
      scrollToFirstStep = false,
      steps = [],
      i18n
    } = this.props;
    const step = getMergedStep(steps[index], this.props);
    let output;
    if (status === STATUS$1.RUNNING && step) {
      output = /*#__PURE__*/React.createElement(Step, _extends({}, this.state, {
        onChange: this.handleChange,
        continuous: (steps === null || steps === void 0 ? void 0 : steps.length) > 1,
        setPopper: this.setPopper,
        helpers: this.helpers,
        isMobile: isMobile,
        shouldScroll: !disableScrolling && (index !== 0 || scrollToFirstStep),
        step: step,
        update: this.handleUpdate,
        closeButtonSrLabel: closeButtonSrLabel || 'Close',
        id: "".concat(id, "-step"),
        innerRef: innerRef,
        i18n: i18n
      }));
    }
    return output ? /*#__PURE__*/_jsx(Portal, {
      onMounted: this.onPortalMount,
      className: "andes-coach-marks"
    }, void 0, output) : null;
  }
}
var CoachMarks$1 = CoachMarks;
var module$2 = "@andes/coach-marks";
var locale$2 = "en-US";
var translations$2 = {
  "andes-coach-marks\u0004de": [null, "from"],
  "andes-coach-marks\u0004close": [null, "Close"]
};
var enTranslations = {
  module: module$2,
  locale: locale$2,
  translations: translations$2
};
var module$1 = "@andes/coach-marks";
var locale$1 = "es-AR";
var translations$1 = {
  "andes-coach-marks\u0004de": [null, "de"],
  "andes-coach-marks\u0004close": [null, "Cerrar"]
};
var esTranslations = {
  module: module$1,
  locale: locale$1,
  translations: translations$1
};
var module = "@andes/coach-marks";
var locale = "pt-BR";
var translations = {
  "andes-coach-marks\u0004de": [null, "de"],
  "andes-coach-marks\u0004close": [null, "Fechar"]
};
var ptTranslations = {
  module: module,
  locale: locale,
  translations: translations
};
const CoachMarksContainer = /*#__PURE__*/function () {
  const CoachMarksContainer = /*#__PURE__*/forwardRef((props, ref) => {
    const {
      id,
      isMobile: isMobileProp
    } = props;
    const componentId = useId(id);
    const i18n = getPatchedI18n(AndesContext, enTranslations, esTranslations, ptTranslations);
    const {
      device
    } = useContext(AndesContext);
    const isMobile = (device === null || device === void 0 ? void 0 : device.mobile) || isMobileProp || false;
    return /*#__PURE__*/React.createElement(CoachMarks$1, _extends({}, props, {
      innerRef: ref,
      id: componentId,
      i18n: i18n,
      isMobile: isMobile
    }));
  });
  CoachMarksContainer.displayName = 'CoachMarksContainer';
  return CoachMarksContainer;
}();
var CoachMarksContainer$1 = CoachMarksContainer;
export { CoachMarksContainer$1 as CoachMarks, CoachMarksContainer$1 as default };
